import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../../components/profile-components/Navbar";
import Topbar from "../../components/profile-components/Topbar";
import API_BASE_URL from "../../config";
import "../SalesPages.css";
import "../Settings.css";
import "../../styles/Tabs.css";
import "./CoverageTypes.css";

const CoverageTypes = () => {
  const [coverageTypes, setCoverageTypes] = useState([]);
  const [name, setName] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [selectedType, setSelectedType] = useState(null);
  const [editedName, setEditedName] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const token = localStorage.getItem("token");

  const filteredCoverageTypes = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    if (!term) return coverageTypes;

    return coverageTypes.filter((type) =>
      String(type.name || "").toLowerCase().includes(term)
    );
  }, [coverageTypes, searchTerm]);

  useEffect(() => {
    loadCoverageTypes();
  }, []);

  const clearMessages = () => {
    setError("");
    setSuccess("");
  };

  const loadCoverageTypes = async () => {
    try {
      setLoading(true);
      clearMessages();

      const res = await fetch(`${API_BASE_URL}/api/coverage-types`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data.error || data.message || "Failed to load coverage types.");
      }

      setCoverageTypes(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching coverage types:", err);
      setError(err.message || "Failed to load coverage types.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const names = name
      .split("\n")
      .map((item) => item.trim())
      .filter(Boolean);

    if (!names.length) {
      setError("Please enter at least one coverage type name.");
      setSuccess("");
      return;
    }

    try {
      setSaving(true);
      clearMessages();

      const addedTypes = [];

      for (const typeName of names) {
        const res = await fetch(`${API_BASE_URL}/api/coverage-types`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ name: typeName })
        });

        const data = await res.json().catch(() => ({}));

        if (!res.ok) {
          throw new Error(data.error || data.message || `Failed to add ${typeName}.`);
        }

        addedTypes.push({
          id: data.id,
          name: data.name || typeName
        });
      }

      setCoverageTypes((prev) => [...addedTypes, ...prev]);
      setName("");
      setSuccess(`${addedTypes.length} coverage type(s) added successfully.`);
    } catch (err) {
      console.error("Error adding coverage types:", err);
      setError(err.message || "Failed to add coverage type(s).");
      setSuccess("");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!selectedType) return;

    const cleanName = editedName.trim();

    if (!cleanName) {
      setError("Coverage type name is required.");
      return;
    }

    try {
      setSaving(true);
      clearMessages();

      const res = await fetch(`${API_BASE_URL}/api/coverage-types/${selectedType.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ name: cleanName })
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data.error || data.message || "Failed to update coverage type.");
      }

      setCoverageTypes((prev) =>
        prev.map((type) =>
          type.id === selectedType.id ? { ...type, name: cleanName } : type
        )
      );

      setSelectedType(null);
      setEditedName("");
      setSuccess("Coverage type updated successfully.");
    } catch (err) {
      console.error("Coverage type update error:", err);
      setError(err.message || "Update failed.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedType) return;

    if (!window.confirm(`Delete ${selectedType.name}?`)) return;

    try {
      setSaving(true);
      clearMessages();

      const res = await fetch(`${API_BASE_URL}/api/coverage-types/${selectedType.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data.error || data.message || "Failed to delete coverage type.");
      }

      setCoverageTypes((prev) => prev.filter((type) => type.id !== selectedType.id));
      setSelectedType(null);
      setEditedName("");
      setSuccess("Coverage type deleted successfully.");
    } catch (err) {
      console.error("Coverage type delete error:", err);
      setError(err.message || "Delete failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Navbar />
      <Topbar />

      <div className="main-content sales-page settings-page-modern">
        <div className="sales-hero sales-profile-hero">
          <div>
            <p className="sales-eyebrow">Settings</p>
            <h1 className="sales-title">Coverage Types</h1>
            <p className="sales-subtitle">
              Add and manage coverage categories used throughout the CRM.
            </p>
          </div>

          <div className="sales-header-actions">
            <Link
              className="sales-secondary-btn"
              to="/settings?tab=Data%20%26%20Customization"
              style={{ textDecoration: "none" }}
            >
              ← Back to Data & Customization
            </Link>

            <button
              type="button"
              className="sales-secondary-btn"
              onClick={loadCoverageTypes}
              disabled={loading}
            >
              {loading ? "Refreshing..." : "Refresh"}
            </button>
          </div>
        </div>

        {(error || success) && (
          <div
            className={`sales-section-card coverage-message-card ${
              error ? "coverage-message-error" : "coverage-message-success"
            }`}
          >
            {error || success}
          </div>
        )}

        <div className="coverage-types-grid">
          <div className="sales-section-card sales-section-subscription">
            <div className="sales-section-head">
              <h2>Add Coverage Types</h2>
              <p>Enter one coverage type per line.</p>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="sales-form-fields">
                <div className="sales-field sales-field-full">
                  <label>Coverage Type Names</label>
                  <textarea
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    placeholder={"MAPD\nLife\nPDP\nHospital Indemnity"}
                    rows={8}
                  />
                </div>
              </div>

              <div className="settings-inline-actions coverage-actions">
                <button
                  type="submit"
                  className="sales-primary-btn"
                  disabled={saving}
                >
                  {saving ? "Saving..." : "Add Coverage Type(s)"}
                </button>

                <button
                  type="button"
                  className="sales-secondary-btn"
                  onClick={() => {
                    setName("");
                    clearMessages();
                  }}
                  disabled={saving}
                >
                  Clear
                </button>
              </div>
            </form>
          </div>

          <div className="sales-section-card sales-section-lead">
            <div className="sales-section-head">
              <h2>Existing Coverage Types</h2>
              <p>Search, edit, or delete coverage types.</p>
            </div>

            <div className="sales-form-fields coverage-search-row">
              <div className="sales-field sales-field-full">
                <label>Search</label>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder="Search coverage types..."
                />
              </div>
            </div>

            <div className="coverage-table-wrap">
              {filteredCoverageTypes.length > 0 ? (
                <table className="coverage-table">
                  <thead>
                    <tr>
                      <th>Coverage Type</th>
                      <th className="coverage-table-action-col">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCoverageTypes.map((type) => (
                      <tr
                        key={type.id}
                        onClick={() => {
                          setSelectedType(type);
                          setEditedName(type.name || "");
                        }}
                      >
                        <td>{type.name}</td>
                        <td className="coverage-table-action-col">
                          <button
                            type="button"
                            className="coverage-table-edit-btn"
                            onClick={(event) => {
                              event.stopPropagation();
                              setSelectedType(type);
                              setEditedName(type.name || "");
                            }}
                          >
                            Edit
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="coverage-empty-state">
                  {loading ? "Loading coverage types..." : "No coverage types found."}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {selectedType && (
        <div
          className="modal-overlay"
          onClick={() => {
            setSelectedType(null);
            setEditedName("");
          }}
        >
          <div className="modal coverage-edit-modal" onClick={(event) => event.stopPropagation()}>
            <h3>Edit Coverage Type</h3>

            <div className="sales-field sales-field-full">
              <label>Coverage Type Name</label>
              <input
                type="text"
                value={editedName}
                onChange={(event) => setEditedName(event.target.value)}
                placeholder="Coverage type name"
              />
            </div>

            <div className="modal-actions coverage-modal-actions">
              <button
                type="button"
                className="modal-button save"
                onClick={handleSaveEdit}
                disabled={saving}
              >
                {saving ? "Saving..." : "Save"}
              </button>

              <button
                type="button"
                className="modal-button delete"
                onClick={handleDelete}
                disabled={saving}
              >
                Delete
              </button>

              <button
                type="button"
                className="modal-button neutral"
                onClick={() => {
                  setSelectedType(null);
                  setEditedName("");
                }}
                disabled={saving}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CoverageTypes;